import { findFreePlacement, cloneGrid, type GridState } from "../inventory/grid.js";
import type { Rng } from "../math/rng.js";
import { getItemDef } from "./db.js";
import { mintFactoryMods } from "./mods.js";
import type { ItemInstance } from "./types.js";

/**
 * What is in the cupboard. A loot table is a weighted bag plus a roll count; a
 * container is a table id and a box (`LootSpawnDef`). The map says what KIND of
 * place this is and the table what it contains, which is why mapgen can stock a
 * village it has never seen. Six overlapping tables, so route knowledge is a
 * probability rather than a checklist.
 */

export interface LootEntry {
  defId: string;
  /** Relative odds within the table. Any positive number; ratios are what matter. */
  weight: number;
  /** Stack size range for a stackable item. Both default to 1. */
  stackMin?: number;
  stackMax?: number;
}

export interface LootTable {
  id: string;
  /** How many entries are drawn, inclusive on both ends. */
  rolls: { min: number; max: number };
  entries: LootEntry[];
}

/**
 * The tables. No "valuables" table on purpose: the church chest draws from
 * `household`, which carries the pocket watch and the crucifix at low weight.
 */
const TABLES: LootTable[] = [
  {
    // The Spar, the Gasthaus kitchen, the sennerei: food, drink, small change.
    id: "groceries",
    rolls: { min: 2, max: 4 },
    entries: [
      { defId: "speck", weight: 18, stackMin: 1, stackMax: 2 },
      { defId: "bergkaese", weight: 12 },
      { defId: "honigglas", weight: 14, stackMin: 1, stackMax: 2 },
      { defId: "schnaps", weight: 12 },
      { defId: "zigaretten", weight: 14, stackMin: 1, stackMax: 3 },
      { defId: "streichhoelzer", weight: 12, stackMin: 1, stackMax: 4 },
      { defId: "feuerzeug", weight: 10, stackMin: 1, stackMax: 2 },
      { defId: "euro", weight: 8, stackMin: 20, stackMax: 120 },
    ],
  },
  {
    // Living rooms, sacristies, hotel rooms: odds, ends, and the occasional
    // thing somebody will miss.
    id: "household",
    rolls: { min: 2, max: 4 },
    entries: [
      { defId: "batterie", weight: 16, stackMin: 1, stackMax: 2 },
      { defId: "feuerzeug", weight: 14, stackMin: 1, stackMax: 2 },
      { defId: "streichhoelzer", weight: 14, stackMin: 1, stackMax: 5 },
      { defId: "zigaretten", weight: 12, stackMin: 1, stackMax: 2 },
      { defId: "honigglas", weight: 10 },
      { defId: "schnaps", weight: 8 },
      { defId: "euro", weight: 10, stackMin: 10, stackMax: 200 },
      { defId: "med_verband", weight: 6 },
      { defId: "taschenuhr", weight: 4 },
      { defId: "silberkreuz", weight: 1 },
    ],
  },
  {
    // The Feuerwehr, the sawmill, every garage in the village.
    id: "tools",
    rolls: { min: 2, max: 3 },
    entries: [
      { defId: "naegel", weight: 18, stackMin: 1, stackMax: 3 },
      { defId: "schraubenzieher", weight: 16 },
      { defId: "kupferdraht", weight: 14, stackMin: 1, stackMax: 2 },
      { defId: "zuendkerzen", weight: 14, stackMin: 1, stackMax: 3 },
      { defId: "batterie", weight: 12, stackMin: 1, stackMax: 3 },
      { defId: "jagdmesser", weight: 8 },
      { defId: "benzinkanister", weight: 5 },
      { defId: "euro", weight: 6, stackMin: 10, stackMax: 80 },
      { defId: "med_verband", weight: 4 },
      // Workshop parts: what a village gunsmith screws on with a vice and a
      // file. The can is the one thing here nobody makes at home.
      { defId: "muzzle_brake_8x57", weight: 3 },
      { defId: "ported_choke_12g", weight: 3 },
      { defId: "foregrip", weight: 3 },
      { defId: "suppressor_9x19", weight: 1 },
      // Rail furniture belongs in a garage rather than a gun cabinet.
      { defId: "flashlight", weight: 4 },
      { defId: "smg_rail_foreend", weight: 2 },
      { defId: "railed_handguard", weight: 2 },
      { defId: "smg_barrel_short", weight: 2 },
      { defId: "slant_brake", weight: 2 },
      { defId: "laser", weight: 1 },
      // The fire station's signal canister — the only grenade found outside a
      // gun
      // cabinet, and the only one that is not ordnance.
      { defId: "gren_smoke", weight: 2 },
    ],
  },
  {
    // Barns and farmhouses: what a working farm actually has lying about.
    id: "farm",
    rolls: { min: 2, max: 4 },
    entries: [
      { defId: "speck", weight: 18, stackMin: 1, stackMax: 2 },
      { defId: "bergkaese", weight: 14 },
      { defId: "naegel", weight: 14, stackMin: 1, stackMax: 3 },
      { defId: "kupferdraht", weight: 12 },
      { defId: "honigglas", weight: 12, stackMin: 1, stackMax: 2 },
      { defId: "schnaps", weight: 10 },
      { defId: "jagdmesser", weight: 8 },
      { defId: "benzinkanister", weight: 4 },
      { defId: "ammo_12g_buck", weight: 6, stackMin: 2, stackMax: 8 },
      { defId: "euro", weight: 6, stackMin: 10, stackMax: 60 },
    ],
  },
  {
    // Schools, the Gemeindeamt, the fire station's crew room.
    id: "medical",
    rolls: { min: 1, max: 3 },
    entries: [
      { defId: "med_verband", weight: 24 },
      { defId: "med_schiene", weight: 16 },
      { defId: "med_erstehilfe", weight: 12 },
      { defId: "med_sanikasten", weight: 4 },
      { defId: "euro", weight: 8, stackMin: 10, stackMax: 60 },
      { defId: "batterie", weight: 6 },
    ],
  },
  {
    // Gun cabinets: the only source of armour and weapons, rare on purpose — a
    // rifle everybody finds means nothing.
    id: "hunting",
    rolls: { min: 1, max: 3 },
    entries: [
      { defId: "ammo_8x57_fmj", weight: 20, stackMin: 5, stackMax: 30 },
      { defId: "ammo_8x57_sp", weight: 14, stackMin: 5, stackMax: 20 },
      { defId: "ammo_12g_buck", weight: 14, stackMin: 4, stackMax: 12 },
      { defId: "ammo_12g_slug", weight: 10, stackMin: 3, stackMax: 10 },
      { defId: "ammo_9x19_fmj", weight: 12, stackMin: 10, stackMax: 40 },
      { defId: "ammo_9x19_hp", weight: 8, stackMin: 5, stackMax: 20 },
      // The fourth calibre: plenty of steel case about, the soft-point reload
      // rarer.
      { defId: "ammo_762_fmj", weight: 8, stackMin: 5, stackMax: 30 },
      { defId: "ammo_762_hp", weight: 5, stackMin: 5, stackMax: 20 },
      { defId: "jagdmesser", weight: 10 },
      { defId: "rig_patronengurt", weight: 6 },
      { defId: "rig_brustgurt", weight: 2 },
      { defId: "pack_wander", weight: 5 },
      { defId: "helmet_stahl", weight: 5 },
      { defId: "vest_loden", weight: 4 },
      { defId: "shotgun", weight: 4 },
      { defId: "carbine", weight: 3 },
      { defId: "pistol", weight: 3 },
      { defId: "smg", weight: 2 },
      { defId: "helmet_gendarm", weight: 2 },
      { defId: "dmr", weight: 1 },
      { defId: "vest_platte", weight: 1 },
      // Sights, stocks and magazines share the cabinet. Weights low on purpose:
      // a
      // cabinet that mostly yields parts stops being exciting.
      { defId: "red_dot", weight: 4 },
      { defId: "foregrip", weight: 3 },
      { defId: "recoil_pad", weight: 3 },
      { defId: "shotgun_tube_8", weight: 3 },
      { defId: "ported_choke_12g", weight: 2 },
      { defId: "scope_2x", weight: 2 },
      { defId: "scope_4x", weight: 2 },
      // The 8× is the one optic no trader stocks; weight 1 — the find of a
      // raid, not a rumour.
      { defId: "scope_8x", weight: 1 },
      { defId: "thumbhole_stock", weight: 2 },
      { defId: "carbine_mag_40", weight: 2 },
      { defId: "smg_drum_45", weight: 1 },
      // The hunting rifle's deep box: the one magazine no trader stocks, and
      // the factory ones come fitted to the guns above rather than loose.
      { defId: "rifle_mag_10", weight: 1 },
      // Barrels are cabinet items (a rebarrel is a gunsmith's job); the Eastern
      // carbine at the DMR's weight. Its factory parts arrive fitted, so they
      // are
      // not listed.
      { defId: "carbine_barrel_long", weight: 2 },
      { defId: "shotgun_barrel_coach", weight: 2 },
      { defId: "ec_mag_30", weight: 2 },
      { defId: "rifle_barrel_match", weight: 1 },
      { defId: "railed_top_cover", weight: 1 },
      { defId: "ergo_grip", weight: 1 },
      { defId: "east_carbine", weight: 1 },
      // The sniper rifle at the hunting rifle's weight. The cabinet is the only
      // way
      // in for the two parts below it — the ten-power glass and the short
      // barrel —
      // which stay off every shelf in the valley.
      { defId: "sniper", weight: 1 },
      { defId: "sniper_scope_10x", weight: 1 },
      { defId: "sniper_barrel_short", weight: 1 },
      // Ordnance: a frag is worth a raid like the 8×, the flashbang is the one
      // somebody actually uses.
      { defId: "gren_flash", weight: 2 },
      { defId: "gren_smoke", weight: 2 },
      { defId: "gren_frag", weight: 1 },
    ],
  },
];

export const LOOT_TABLES: ReadonlyMap<string, LootTable> = new Map(
  TABLES.map((t) => [t.id, t]),
);

/** Every table id, in declaration order. What mapgen's mapping must map onto. */
export const LOOT_TABLE_IDS: readonly string[] = TABLES.map((t) => t.id);

/** Non-throwing lookup — table ids arrive from map data, which is untrusted. */
export function getLootTable(id: string): LootTable | null {
  return LOOT_TABLES.get(id) ?? null;
}

/**
 * Rolls one container's contents into `grid`. Pure: returns a NEW grid, and
 * rolling into a non-empty grid is additive (a corpse's rig gets scav loot on
 * top of what it carried).
 *
 * DETERMINISM CONTRACT — the draw schedule is part of the API, since the server
 * rolls every container from the raid seed: ONE draw for the roll count, then
 * per roll one draw for the entry and one for the stack size (taken
 * UNCONDITIONALLY, even for a non-stacking item). Exactly `1 + 2 * count`
 * draws. Adding an ENTRY keeps the schedule but changes the outcome; changing
 * `rolls` or the draw order must never happen quietly. An unknown or
 * unplaceable entry is skipped SILENTLY after its two draws.
 */
export function rollContainerLoot(
  tableId: string,
  grid: GridState,
  rng: Rng,
  nextIid: () => string,
): GridState {
  const table = getLootTable(tableId);
  if (table === null) return cloneGrid(grid);

  const lo = table.rolls.min;
  const hi = table.rolls.max;
  const count = rng.int(lo, hi);

  const out = cloneGrid(grid);
  let totalWeight = 0;
  for (const e of table.entries) totalWeight += e.weight > 0 ? e.weight : 0;
  if (totalWeight <= 0 || table.entries.length === 0) return out;

  for (let i = 0; i < count; i++) {
    // (a) weighted pick — one draw.
    let pick = rng.next() * totalWeight;
    let entry: LootEntry = table.entries[table.entries.length - 1] as LootEntry;
    for (const e of table.entries) {
      const w = e.weight > 0 ? e.weight : 0;
      if (pick < w) {
        entry = e;
        break;
      }
      pick -= w;
    }

    // (b) stack size — one draw, whatever the entry turned out to be.
    const sMin = entry.stackMin ?? 1;
    const sMax = entry.stackMax ?? sMin;
    const rolled = rng.int(sMin, sMax);

    const def = getItemDef(entry.defId);
    if (def === null) continue;
    const stack = clampStack(rolled, def.stackMax);
    const at = findFreePlacement(out, def);
    if (at === null) continue;

    const item: ItemInstance = { iid: nextIid(), defId: def.id, stack };
    if (def.kind === "armor") item.durability = def.maxDurability;
    else if (def.kind === "med") item.durability = def.charges;
    // A found gun is found EMPTY (ammunition is what you look for after the
    // gun)
    // but ASSEMBLED — every factory part, the magazine empty, byte-identical to
    // one off a shelf. The extra `nextIid` calls are outside the draw schedule.
    else if (def.kind === "weapon") {
      const mods = mintFactoryMods(def, nextIid);
      if (mods !== undefined) item.mods = mods;
    }

    out.items.push({ item, x: at.x, y: at.y, rot: at.rot });
  }

  return out;
}

function clampStack(n: number, max: number): number {
  if (!Number.isFinite(n) || n < 1) return 1;
  const i = Math.floor(n);
  return i > max ? max : i;
}

/**
 * Structural check for the tables: every entry names a real item, weights are
 * positive, stack ranges are sane and within the def's `stackMax`. Empty means
 * sound.
 */
export function validateLootTables(): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();

  for (const t of TABLES) {
    const where = `loot table "${t.id}"`;
    if (t.id.length === 0) problems.push("a loot table has an empty id");
    if (seen.has(t.id)) problems.push(`duplicate loot table id "${t.id}"`);
    seen.add(t.id);

    if (!Number.isInteger(t.rolls.min) || t.rolls.min < 0) problems.push(`${where} has a bad rolls.min`);
    if (!Number.isInteger(t.rolls.max) || t.rolls.max < t.rolls.min) {
      problems.push(`${where} has rolls.max < rolls.min`);
    }
    if (t.entries.length === 0) problems.push(`${where} has no entries`);

    for (const e of t.entries) {
      const def = getItemDef(e.defId);
      if (def === null) {
        problems.push(`${where} references unknown item "${e.defId}"`);
        continue;
      }
      if (!(e.weight > 0)) problems.push(`${where} entry "${e.defId}" has weight <= 0`);
      const sMin = e.stackMin ?? 1;
      const sMax = e.stackMax ?? sMin;
      if (!Number.isInteger(sMin) || sMin < 1) problems.push(`${where} entry "${e.defId}" has a bad stackMin`);
      if (!Number.isInteger(sMax) || sMax < sMin) problems.push(`${where} entry "${e.defId}" has stackMax < stackMin`);
      if (sMax > def.stackMax) {
        problems.push(
          `${where} entry "${e.defId}" rolls up to ${sMax} but the item caps at ${def.stackMax}`,
        );
      }
    }
  }
  return problems;
}
